import { useEffect, useState } from "react";
import "@/App.css";
import Splash from "@/components/Splash";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TimerProvider } from "@/context/TimerContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Workflow from "@/pages/Workflow";
import DailyTracker from "@/pages/DailyTracker";
import Dashboard from "@/pages/Dashboard";
import Calendar from "@/pages/Calendar";
import AccessControl from "@/pages/AccessControl";
import Profile from "@/pages/Profile";
import Home from "@/pages/Home";
import Archive from "@/pages/Archive";
import SchoolMaster from "@/pages/SchoolMaster";
import { canAccessControl, canViewDashboard, isAdmin } from "@/lib/roles";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  return <Layout />;
}

function AccessRoute({ children }) {
  const { user, viewMode } = useAuth();
  const employeeView = isAdmin(user) && viewMode === "employee";
  if (user && (!canAccessControl(user) || employeeView)) return <Navigate to="/home" replace />;
  return children;
}

function DashboardRoute({ children }) {
  const { user } = useAuth();
  if (user && !canViewDashboard(user)) return <Navigate to="/workflow" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/home" element={<Home />} />
        <Route path="/dashboard" element={<DashboardRoute><Dashboard /></DashboardRoute>} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/workflow" element={<Workflow />} />
        <Route path="/daily-tracker" element={<DailyTracker />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/schools" element={<AccessRoute><SchoolMaster /></AccessRoute>} />
        <Route path="/access-control" element={<AccessRoute><AccessControl /></AccessRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemeInit() {
  useEffect(() => {
    const t = localStorage.getItem("theme");
    if (t === "dark") document.documentElement.classList.add("dark");
  }, []);
  return null;
}

function App() {
  const [splashDone, setSplashDone] = useState(false);
  const [splashGone, setSplashGone] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setSplashDone(true), 1900);
    const t2 = setTimeout(() => setSplashGone(true), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TimerProvider>
            <ThemeInit />
            {!splashGone && <Splash done={splashDone} />}
            <AppRouter />
            <Toaster position="top-right" richColors />
          </TimerProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
